import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '../../lib/utils';
import { useCanvasSize, getCanvasScale } from './CanvasSizeContext';
import type { DroppableState } from './DroppableZone';

export interface HotspotMarkerProps {
    id: string;
    x: number;
    y: number;
    radius: number;
    label?: string;
    state?: DroppableState;
    isDragActive?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    children?: React.ReactNode;
    className?: string;
}

export const HotspotMarker: React.FC<HotspotMarkerProps> = ({
    id,
    x,
    y,
    radius,
    label,
    state = 'default',
    isDragActive = false,
    disabled = false,
    onClick,
    children,
    className,
}) => {
    const { setNodeRef, isOver } = useDroppable({ id });
    const canvasSize = useCanvasSize();

    const showDropHint = isDragActive && !disabled && state === 'default';
    const baseDiameter = radius * 2;
    const diameter = baseDiameter * getCanvasScale(canvasSize);

    return (
        <div
            ref={setNodeRef}
            className={cn(
                'absolute flex items-center justify-center rounded border-2 transition-all duration-200 overflow-hidden',
                state === 'active' && 'border-primary',
                state === 'correct' && 'border-success bg-success/20',
                state === 'incorrect' && 'border-destructive bg-destructive/20 animate-shake',
                showDropHint && !isOver && 'border-dashed border-primary/60 bg-primary/10',
                isOver && !disabled && 'ring-2 ring-primary ring-offset-2 ring-offset-background bg-primary/20 border-primary',
                disabled ? 'cursor-default' : 'cursor-pointer',
                className
            )}
            style={{
                left: `${x}%`,
                top: `${y}%`,
                width: diameter,
                height: diameter,
                marginLeft: -diameter / 2,
                marginTop: -diameter / 2,
            }}
            onClick={() => !disabled && onClick?.()}
            title={label}
            aria-label={label}
        >
            {children}
        </div>
    );
};

HotspotMarker.displayName = 'HotspotMarker';
